// ===========================================
// THEME CONFIGURATION
// ===========================================

/**
 * Theme configuration for customization
 */
export const themeConfig = {
  // Color palette
  colors: {
    // Primary brand colors
    primary: {
      50: '#e6f5ff',
      100: '#b3e0ff',
      200: '#80cbff',
      300: '#4db6ff',
      400: '#1aa1ff',
      500: '#0086da', // Main primary - sky blue
      600: '#0077c2',
      700: '#0068aa',
      800: '#005992',
      900: '#004a7a',
    },

    // Secondary colors (dark gray/charcoal)
    secondary: {
      50: '#f5f5f6',
      100: '#e8e9ea',
      200: '#d1d3d5',
      300: '#b9bdc0',
      400: '#a2a7ab',
      500: '#2d3134', // Main secondary - dark charcoal
      600: '#262a2d',
      700: '#1f2326',
      800: '#181c1f',
      900: '#111518',
    },

    // Accent colors (medium gray)
    accent: {
      50: '#f8f9f9',
      100: '#eff0f1',
      200: '#dfe1e3',
      300: '#cfd2d5',
      400: '#bfc3c7',
      500: '#5b5f62', // Main accent - medium gray
      600: '#52565a',
      700: '#494d51',
      800: '#404448',
      900: '#373b3f',
    },

    // Neutral colors
    neutral: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
    },

    // Semantic colors
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    
    // Additional specific colors from the design
    headerGradient: 'rgba(74,96,116,0.8)',
    darkFooter: '#262a36',
    cardBackground: '#E9E9E933', // Semi-transparent gray for card backgrounds
  },

  // Typography
  typography: {
    fontFamily: {
      base: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      heading: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      mono: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
      arabic: "'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif",
    },
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem', // 48px
      '6xl': '3.75rem', // 60px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Spacing scale
  spacing: {
    0: '0',
    1: '0.25rem', // 4px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    5: '1.25rem', // 20px
    6: '1.5rem', // 24px
    8: '2rem', // 32px
    10: '2.5rem', // 40px
    12: '3rem', // 48px
    16: '4rem', // 64px
    20: '5rem', // 80px
    24: '6rem', // 96px
  },

  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.125rem', // 2px
    base: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '0.75rem', // 12px
    '2xl': '1rem', // 16px
    '3xl': '1.5rem', // 24px
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  },

  // Breakpoints
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // Z-index scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
    toast: 1080,
  },

  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    base: '200ms ease-in-out',
    slow: '300ms ease-in-out',
    slower: '500ms ease-in-out',
  },

  // Container max widths
  container: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1440px',
    max: '1400px', // Main container max width used in components
  },

  // Component-specific configurations
  components: {
    header: {
      height: '5rem', // 80px (h-20)
      logoHeight: '4rem', // h-16
    },
    heroSection: {
      minHeight: '100vh',
      titleSize: {
        mobile: '3rem', // text-5xl
        desktop: '4.5rem', // text-7xl
      },
      subtitleSize: {
        mobile: '1.25rem', // text-xl
        desktop: '1.5rem', // text-2xl
      },
    },
    card: {
      borderRadius: {
        default: '1rem', // rounded-2xl
        large: '1.25rem', // rounded-[20px]
      },
      shadow: {
        default: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        hover: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
      },
    },
    button: {
      borderRadius: '9999px', // rounded-full
      padding: {
        small: '0.5rem 1.5rem', // px-6 py-2
        medium: '0.75rem 3rem', // px-12 py-3
        large: '1rem 3rem',
      },
    },
    tourCard: {
      imageHeight: '16rem', // h-64
    },
    packageCard: {
      height: {
        small: '250px',
        medium: '250px',
        large: '526px',
      },
    },
    statsCard: {
      iconSize: '1.5rem', // 24px
      iconPadding: '0.75rem', // p-3
    },
  },
} as const;

/**
 * Get CSS custom properties from theme
 */
export const getCSSVariables = () => {
  return {
    '--color-primary': themeConfig.colors.primary[500],
    '--color-primary-light': themeConfig.colors.primary[400],
    '--color-primary-dark': themeConfig.colors.primary[600],
    '--color-secondary': themeConfig.colors.secondary[500],
    '--color-accent': themeConfig.colors.accent[500],
    '--color-success': themeConfig.colors.success,
    '--color-warning': themeConfig.colors.warning,
    '--color-error': themeConfig.colors.error,
    '--color-info': themeConfig.colors.info,
    '--font-family-base': themeConfig.typography.fontFamily.base,
    '--font-family-heading': themeConfig.typography.fontFamily.heading,
    '--shadow-sm': themeConfig.shadows.sm,
    '--shadow-md': themeConfig.shadows.md,
    '--shadow-lg': themeConfig.shadows.lg,
    '--transition-fast': themeConfig.transitions.fast,
    '--transition-base': themeConfig.transitions.base,
  };
};

export type ThemeConfig = typeof themeConfig;
